import { useEffect, useMemo, useState } from 'react';
import { formatHours, worklogSchema, type Worklog } from '@forecastify/shared';
import {
  Button,
  DataList,
  Dialog,
  TextInput,
  type DataListColumn,
  type DataListRow,
  type SortState,
} from '@forecastify/ui';
import { useT } from '../../i18n';
import { useCalendarLabels } from '../date-field';
import { useSession } from '../../session';
import { Field } from '../card';
import { parseList } from '../permissions-hook';
import { sortInMemory } from '../sort-in-memory';
import { listLabels } from './requests-screen';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ÚJ TÚLÓRA-KÉRELEM — the claim you can raise from the requests page.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The overtime tab had no CTA at all, and its empty state said so: *"overtime is marked on a logged
 * entry"*. True, and it left the one page named *Kérelmeim* unable to make the second kind of
 * request it lists — you had to know to go to Időrögzítés, find the row and open its kebab menu.
 *
 * ⚠️⚠️ **THE SHAPE STAYS THE SERVER'S: A CLAIM IS ONE WORKLOG.** `raiseOvertimeRequestSchema` takes
 * a worklog id and reads the date, the hours and the person off it — there is no arm in which
 * somebody types an hour count, and this dialog must not invent one. So the form is a PICKER over
 * hours that already exist, which is why it loads them rather than offering fields.
 *
 * ⚠️ **Already-claimed hours are not offered.** One claim per worklog is a database constraint and
 * the server answers `already_claimed`; listing a row that cannot be picked would be a control that
 * looks available and refuses. 31 Oct 2026 · ⚠️ **read off the hour itself** (`worklog.overtime`) —
 * it came from the claim list the page held, which is one page now. A WITHDRAWN claim does not hold
 * the hour (`overtime.service.ts`), so it is offered again.
 *
 * ⚠️⚠️ **IT IS A `DataList`, AND THE HAND-ROLLED ONE IT REPLACED LASTED AN HOUR.** That build had
 * its own search box, its own two `Select`s, its own pair of date fields and its own row of sort
 * buttons — every one of them a second implementation of something the shared list already does,
 * and none of them looking like the rest of the product. *"Why is this yet another way of doing
 * this when we already have a table view that fits here?"* The answer was that it was inside a
 * dialog, which is not a reason: `DataList` is a component, and a dialog is a box.
 */

/** How far back the picker looks. Ninety days is a quarter — past that, raise it from the log. */
const WINDOW_DAYS = 90;

export interface NewClaimDraft {
  readonly worklogId: string;
  /**
   * ⚠️⚠️ **AND NO TYPE — `0147`.** This dialog used to offer *Túlóra · ×1.5*, *Hétvégi · ×2*
   * and *Ünnepnapi · ×2.5* beside the hour somebody was claiming, which is a person choosing what
   * their own hour is worth. Raising a claim asserts that an hour WAS overtime; which kind it is
   * belongs to whoever approves it, and `request-detail.tsx` is where they say so.
   */
  readonly note: string;
}

export function NewClaimDialog({
  open,
  serverError,
  onClose,
  onSubmit,
}: {
  readonly open: boolean;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (draft: NewClaimDraft) => void;
}) {
  const t = useT();
  const calendar = useCalendarLabels();
  const session = useSession();

  const [logs, setLogs] = useState<readonly Worklog[]>([]);
  /* The hours already claimed — read off each hour, a withdrawn claim not holding it. */
  const claimed = useMemo(
    () =>
      new Set(
        logs
          .filter((log) => log.overtime !== null && log.overtime.status !== 'revoked')
          .map((log) => log.id),
      ),
    [logs],
  );
  const [loading, setLoading] = useState(true);

  const [worklogId, setWorklogId] = useState('');
  const [note, setNote] = useState('');
  const [query, setQuery] = useState('');
  const [projectId, setProjectId] = useState('');
  const [taskKey, setTaskKey] = useState('');
  const [dateRange, setDateRange] = useState('');
  /* ⚠️ Newest first, which is what somebody claiming an hour is looking for. */
  const [sort, setSort] = useState<readonly SortState[]>([{ key: 'date', direction: 'desc' }]);

  const memberId =
    session.workspaces.find((entry) => entry.id === session.workspaceId)?.memberId ?? null;

  /**
   * ⚠️ **Loaded when the dialog OPENS, not with the page.** Ninety days of one person's hours is a
   * read nobody needs until they ask for it, and the list must be current when they do — a claim
   * raised from the time screen a minute ago has to be missing from this picker.
   */
  useEffect(() => {
    if (!open || memberId === null) return;

    setWorklogId('');
    setNote('');
    setQuery('');
    setProjectId('');
    setTaskKey('');
    setDateRange('');
    setSort([{ key: 'date', direction: 'desc' }]);
    setLoading(true);

    const today = new Date();
    const from = new Date(today.getTime() - WINDOW_DAYS * 24 * 60 * 60 * 1000);
    const wire = (date: Date): string => date.toISOString().slice(0, 10);

    void (async () => {
      /* ⚠️ One read since `0147` — the overtime types went with the picker that used them. */
      /* 30 Oct 2026 · every page of the window — `read-all.ts`. */
      const logsResponse = await requestAll(
        session,
        `/worklogs?memberId=${memberId}&from=${wire(from)}&to=${wire(today)}&sort=worked_on:desc`,
      ).catch(() => null);

      /* ⚠️ `parseList` answers null for a body it cannot read, and an empty picker is the honest
         rendering of that — there is nothing here to offer a retry on. */
      const rows =
        logsResponse?.ok === true ? parseList(worklogSchema, await logsResponse.json()) : null;
      if (rows !== null) setLogs(rows);
      setLoading(false);
    })();
  }, [open, memberId, session]);

  /* ⚠️ `from..to`, one string — `list-toolbar.tsx` owns the encoding, and either end may be blank:
     a `from` with no `to` is *since*. */
  const [rangeFrom = '', rangeTo = ''] = dateRange.split('..');

  const offered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const kept = logs
      .filter((log) => !claimed.has(log.id))
      .filter((log) => projectId === '' || (log.projectId ?? '') === projectId)
      .filter((log) => taskKey === '' || log.taskKey === taskKey)
      .filter((log) => rangeFrom === '' || log.workedOn >= rangeFrom)
      .filter((log) => rangeTo === '' || log.workedOn <= rangeTo)
      .filter(
        (log) =>
          needle === '' ||
          `${log.taskKey} ${log.taskTitle} ${log.projectName ?? ''} ${log.note ?? ''} ${log.workedOn}`
            .toLowerCase()
            .includes(needle),
      );

    return sortInMemory(kept, sort, {
      date: (log) => log.workedOn,
      task: (log) => `${log.taskKey} ${log.taskTitle}`,
      project: (log) => log.projectName,
      hours: (log) => Number(log.hours),
    });
  }, [logs, claimed, query, projectId, taskKey, rangeFrom, rangeTo, sort]);

  /* The projects and tasks the offered hours actually touch — a filter listing something with no
     rows behind it is a control that can only produce an empty list. */
  const projects = [
    ...new Map(
      logs
        .filter((log) => !claimed.has(log.id) && log.projectId !== null)
        .map((log) => [log.projectId ?? '', log.projectName ?? '']),
    ),
  ];
  const tasks = [
    ...new Map(
      logs
        .filter((log) => !claimed.has(log.id))
        .map((log) => [log.taskKey, `${log.taskKey} · ${log.taskTitle}`]),
    ),
  ];

  const columns: DataListColumn[] = [
    { key: 'date', label: t('time.column.date'), width: 110, sortKey: 'date' },
    { key: 'task', label: t('time.column.task'), sortKey: 'task' },
    { key: 'project', label: t('dash.filter.project'), width: 170, sortKey: 'project' },
    { key: 'hours', label: t('time.column.hours'), width: 90, align: 'right', sortKey: 'hours' },
  ];

  const rows: DataListRow[] = offered.map((log) => ({
    id: log.id,
    cells: {
      date: { text: log.workedOn, mono: true },
      /* ⚠️ The note under the title, where the list draws a subtitle — it is what the hour was
         actually for, and it is the thing somebody recognises an hour by. */
      task: {
        text: `${log.taskKey} ${log.taskTitle}`,
        sub: log.note ?? undefined,
        /* ⚠️ The picked row carries the accent, because a dialog whose only job is choosing one
           row has to say which one is chosen. */
        bold: log.id === worklogId,
      },
      project: {
        text: log.projectName ?? t('projects.internal'),
        dot: log.projectColor ?? undefined,
      },
      hours: { text: formatHours(log.hours), mono: true },
    },
  }));

  const picked = offered.find((log) => log.id === worklogId);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('leave.claim.new')}
      description={t('leave.claim.newHint')}
      width="min(1000px, 96vw)"
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={worklogId === ''}
            onClick={() => {
              if (worklogId === '') return;
              onSubmit({ worklogId, note: note.trim() });
            }}
          >
            {t('leave.claim.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="max-h-[52vh] min-h-[280px] overflow-hidden rounded-xl border">
          <DataList
            columns={columns}
            rows={rows}
            labels={{ ...listLabels(t), search: t('leave.claim.searchPlaceholder') }}
            search={query}
            onSearchChange={setQuery}
            sort={sort}
            onSortChange={setSort}
            filters={[
              {
                key: 'project',
                label: t('dash.filter.project'),
                value: projectId,
                onChange: setProjectId,
                options: [
                  { value: '', label: t('dash.filter.allProjects') },
                  ...projects.map(([id, name]) => ({ value: id, label: name })),
                ],
              },
              {
                key: 'task',
                label: t('time.column.task'),
                value: taskKey,
                onChange: setTaskKey,
                options: [
                  { value: '', label: t('leave.claim.allTasks') },
                  ...tasks.map(([key, label]) => ({ value: key, label })),
                ],
              },
              {
                /* ⚠️ A DATE range, which is the toolbar's own `input: 'date'` arm — it draws the
                   stepping picker rather than two text boxes, and it needs today to open on. */
                key: 'worked',
                kind: 'range',
                input: 'date',
                calendar,
                today: new Date().toISOString().slice(0, 10),
                label: t('time.column.date'),
                value: dateRange,
                onChange: setDateRange,
                options: [],
              },
            ]}
            onRowClick={setWorklogId}
            /**
             * ┌────────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ ONE REQUEST IS ONE ENTRY — feedback round 4 item 23.                 │
             * └────────────────────────────────────────────────────────────────┘
             *
             * `selectable` DEFAULTS TO TRUE, so this dialog drew a checkbox on every row — a
             * multi-select control beside a single-select one, offering a gesture the dialog has no
             * use for and the endpoint refuses: `POST /overtime` takes one `worklogId`, because a
             * claim is about one hour and carries that hour's own multiplier and approver.
             *
             * ⚠️ **And nothing consumed the selection**, which is the case `data-list.tsx` names at
             * `selectable`: with no `bulkActions` it is a checkbox per row that does nothing, plus
             * a *select all* announced on a table nobody can act on. Clicking the ROW is how a row
             * is chosen here, and the chosen one is the bold line the summary names.
             */
            selectable={false}
            isLoading={loading}
            empty={{
              icon: 'timer',
              title: t('leave.claim.noLogs'),
              body: t('leave.claim.noLogsBody'),
            }}
            footerNote={
              picked === undefined
                ? t('leave.claim.pickLog')
                : t('leave.claim.picked', {
                    task: picked.taskKey,
                    date: picked.workedOn,
                    hours: formatHours(picked.hours),
                  })
            }
          />
        </div>

        {/* ⚠️ The note alone since `0147` took the type field out of this row. */}
        <div className="grid gap-3">
          <Field label={t('leave.field.note')}>
            <TextInput
              value={note}
              placeholder={t('leave.claim.notePlaceholder')}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Field>
        </div>

        {serverError === null ? null : (
          <p className="text-destructive text-[12px]">{serverError}</p>
        )}
      </div>
    </Dialog>
  );
}
